import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { SidebarPricingSaveEvent } from '@app/modules/calendar/components/sidebar-pricing/sidebar-pricing.component';
import { BillingFacade } from '@app/shared/+state/billing';
import { NotificationType } from '@app/shared/interfaces';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { PropertiesService, SimpleProperty } from '@app/shared/services/properties/properties.service';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { mapConflictingEvents } from '@app/shared/utils';
import {
  addDays,
  addMonths,
  addWeeks,
  format as dfnsFormat,
  eachDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameWeek,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { debounceTime, delay, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { ConflictingEventsComponent } from '../../components/dialog-conflicting-events/dialog-conflicting-events.component';
import { PropertyCalendarReferrerService } from '../../services/property-calendar-referrer.service';

import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { DynamicPricingPreview, PricingPreference } from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { EventsService } from '@app/shared/services/events/events.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { BehaviorSubject, combineLatest, fromEvent, lastValueFrom, Subscription } from 'rxjs';
import { DynamicPricingService } from '@app/features/pricing-and-availability';
import { DynamicPricingPropertyErrorModalComponent } from '@app/modules/calendar/components/dynamic-pricing-property-error-modal/dynamic-pricing-property-error-modal.component';
import { IntercomService } from '@app/shared/services/intercom.service';

const DATE_FORMAT = 'YYYY-MM-DD';

const format = (date: Date | string = new Date(), format: string = DATE_FORMAT) => dfnsFormat(date, format);

const DYNAMIC_PRICING_PREVIEW_DATE_RANGE = 365 * 2; // 24 MONTHS

@Component({
  standalone: false,
  selector: 'sbnb-property-calendar',
  templateUrl: './property-calendar.component.html',
  styleUrls: ['./property-calendar.component.scss'],
})
export class PropertyCalendarComponent extends OnDestroyComponent implements OnInit, OnDestroy {
  @ViewChild('calendarContent', { static: false }) calendarContent: ElementRef;
  Math = Math;
  daysTasks: any;
  startOfWeekPref: number;
  perms$ = this.userPermissionsService;
  Features = Features;

  FAILURE_TYPES = {
    price: 'price',
    available: 'availability',
    minimum_length_of_stay: 'min_stay',
  };

  isCS: boolean = this.csService.isCS();
  debugMode$ = this.csService.debugMode;

  referrer = this.referrerService.lastReferrer$;

  properties: SimpleProperty[];

  startDate = subMonths(startOfMonth(new Date()), 3);
  startMonth;
  monthCount = 9;

  math = Math;

  property: any;

  daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  months: string[][] = []; // ISO day strings in subarray per month

  reservations: any = {};
  reservationsArray: any = [];
  conflictingEventsMap: any = {};

  // This loader is triggered on initial price fetch, and when dynamic pricing is enabled or updated
  loadingPricing: boolean;
  pricingAndAvailability: any;

  loadingTasks: boolean;
  tasks: Record<string, unknown> = {};

  selectedReservation: any;
  hoveredReservationId: any;

  selectedDates: string[];
  hoveredDates: string[];

  desiredPrice: number;
  desiredAvailability: boolean = null;
  desiredLengthOfStay: number;
  desiredMaxLengthOfStay: number;
  desiredNote: string;
  daysOfTheWeek: number[];

  loadingMoreMonths: boolean;
  width: number;

  timeout: any;

  saving: boolean;

  mode = 'occupancy';

  hasFinancialAccess = false;
  hasCalendarAccess = false;
  source: string;

  // Follow sidebar's breakpoint
  private isMobile$ = this.deviceDetectionService.observeBreakpoint('(max-width:  1278px)');
  private dynamicPricingEnabled$ = this.dynamicPricingService.dynamicPricingEnabled$;
  public dynamicPricingAvailable$ = combineLatest([this.isMobile$, this.dynamicPricingEnabled$]).pipe(
    map(([isMobile, dynamicPricingEnabled]) => !isMobile && dynamicPricingEnabled)
  );

  public dynamicPricingVisible$ = new BehaviorSubject<boolean>(false);
  public dynamicPriceMap$ = new BehaviorSubject<DynamicPricingPreview>({});
  public dynamicPricingSuggestionLoading = false;
  public $dynamicPricingOptions = this.dynamicPricingService.$config;
  public $dynamicPricingOn = this.dynamicPricingService.$dynamicPricingOn;
  private _dynamic_pricing_initialized = false;
  private _dynamicPricingSuggestionRequestSub?: Subscription;
  private dynamicPricingPusherUpdates$ = this.dynamicPricingService.dynamicPricingPusherUpdates$;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly propertiesService: PropertiesService,
    private readonly reservationService: ReservationService,
    private readonly titleService: TitleService,
    private readonly router: Router,
    private readonly referrerService: PropertyCalendarReferrerService,
    private readonly userPermissionsService: UserPermissionsService,
    private readonly taskService: TasksService,
    private readonly csService: InternalCsService,
    private readonly auth: AuthenticationService,
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
    private readonly billingFacade: BillingFacade,
    private readonly posthogService: PosthogService,
    private deviceDetectionService: DeviceDetectionService,
    private eventsService: EventsService,
    private dynamicPricingService: DynamicPricingService,
    private intercomService: IntercomService
  ) {
    super();
    // Get the property from navigation state if available
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['property']) {
      this.property = navigation.extras.state['property'];
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.width = event.target.innerWidth;
  }

  ngOnInit(): void {
    this.startOfWeekPref = this.auth.getStartOfWeekPreference();

    this.source = this.route.snapshot.queryParamMap.get('source');
    const month = +this.route.snapshot.queryParamMap.get('month');
    const year = +this.route.snapshot.queryParamMap.get('year');
    let startDate;
    let monthsToLoad;

    if (month && year) {
      const currentMonth = +format(new Date(), 'MM');
      const currentYear = +format(new Date(), 'YYYY');
      const isInFuture = +year - currentYear > 0;

      if (isInFuture) {
        const diff = 12 - currentMonth + month;

        if (diff <= 6) {
          this.startMonth = month;
          startDate = startOfMonth(subMonths(new Date(), 3));
          monthsToLoad = month + 6;
        } else {
          this.startMonth = currentMonth;
        }
      } else {
        const diff = +month - currentMonth;
        if (!isInFuture && diff < -3) {
          this.toast.open(
            "Oops, the calendar can't be viewed more than 3 months in the past",
            'Dismiss',
            NotificationType.Error
          );
        } else {
          this.startMonth = month;
          startDate = startOfMonth(new Date(+year, +month - 1, 1));
        }
      }
    }

    this.userPermissionsService.hasAccessTo(Features.PERM_RESERVATION_FINANCIALS).subscribe((res) => {
      this.hasFinancialAccess = res;
    });

    this.userPermissionsService.hasAccessTo(Features.PERM_CALENDAR).subscribe((res) => {
      this.hasCalendarAccess = res;
    });

    if (this.startOfWeekPref === 0) {
      this.daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    }

    this.width = window.innerWidth;

    this.route.params.subscribe(async (res) => {
      this.propertiesService
        .getProperties({
          filterCriteria: [],
          paginate: false,
          transformer: 'simple',
        })
        .subscribe((res) => {
          this.properties = res.data;
        });

      // Fetch property in case we don't already have it from navigation state
      if (!this.property) {
        try {
          const response = await lastValueFrom(this.propertiesService.getProperty(res.id, ['can_manage_availability']));
          if (response.error || !response.id) {
            this.router.navigate(['/dashboard']);
            return;
          }
          this.property = response;
        } catch (error) {
          this.router.navigate(['/dashboard']);
          return;
        }
      }

      this.titleService.manuallySet(`${this.property.name}`);

      this.getReservations(startDate);
      this.loadingPricing = true;
      this.fetchPricingAndAvailability();
      this.fetchTasks();
      this.buildMonths(startDate, monthsToLoad, true);

      // Dynamic pricing subscriptions
      // We intentionally fetch the dynamic config on both desktop and mobile, in case dynamic pricing is already on
      // and the user needs to be able to reset to dynamic pricing in the sidebar
      this.dynamicPricingEnabled$.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe((enabled) => {
        if (enabled && !this._dynamic_pricing_initialized) {
          this._dynamic_pricing_initialized = true;

          this.dynamicPricingService.init(Number(this.route.snapshot.params.id));
          this.dynamicPricingPusherUpdates$
            .pipe(
              filter((updated) => updated),
              takeUntil(this.destroyed$)
            )
            .subscribe(() => {
              // on activation or update, we get this event to tell us that we can refetch fresh data.
              // fetching the preview is mostly useful when the user is updating the dynamic pricing options
              this.fetchDynamicPricingPreview(this.$dynamicPricingOptions()?.current_base_price_type,
                this.$dynamicPricingOptions()?.min_price?.amount,
                this.$dynamicPricingOptions()?.max_price?.amount,
                this.$dynamicPricingOptions()?.current_base_price_type === PricingPreference.CUSTOM ? this.$dynamicPricingOptions()?.base_prices?.custom?.amount : undefined,
              );
              this.fetchPricingAndAvailability();
            });

          this.eventsService
            .on<boolean>(`dynamic-pricing-property-error-modal-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
              this.presentDynamicPricingErrorModal();
            });

          this.eventsService
            .on<{
              base_price_type: 'conservative' | 'recommended' | 'aggressive';
              min_price?: number;
              max_price?: number;
              base_price?: number;
            }>(`dynamic-pricing-preview-fetch-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((data) => {
              const { base_price_type, min_price, max_price, base_price } = data;
              this.previewDynamicPricing(base_price_type, true, min_price, max_price, base_price);
            });

          this.eventsService
            .on(`dynamic-pricing-preview-loading-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((loading: boolean) => {
              this.dynamicPricingSuggestionLoading = loading;
            });

          // TODO: consolidate actions
          this.eventsService
            .on(`dynamic-pricing-disabled-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
              this.dynamicPricingVisible$.next(false);
              this.fetchPricingAndAvailability();
            });

          this.eventsService
            .on<boolean>(`dynamic-pricing-visible-change-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((visible) => {
              this.dynamicPricingVisible$.next(visible);
            });

          this.eventsService
            .on(`dynamic-pricing-enabled-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
              this.dynamicPricingVisible$.next(false);
              // This is purely a visual indicator to the user that their
              // prices have been updated.
              this.loadingPricing = true;
              setTimeout(() => {
                this.loadingPricing = false;
              }, 2100);
            });

          this.eventsService
            .on(`dynamic-pricing-updated-${this.property.id}`)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
              this.loadingPricing = true;
              this.dynamicPricingSuggestionLoading = true;
            });

          this.dynamicPricingVisible$.subscribe((visible) => {
            if (visible) {
              if (this.mode === 'tasks') {
                this.mode = 'occupancy';
              }
              // CLEAR SELECTED DATES WHEN PREVIEW IS ON, WE MIGHT NEED TO CHANGE THIS
              this.clearSelected();
            }
          });
        }
      });

      setTimeout(() => {
        this.timeout = fromEvent(this.calendarContent?.nativeElement, 'scroll')
          .pipe(debounceTime(100), distinctUntilChanged())
          .subscribe((res: UIEvent) => {
            const target: HTMLElement = res.target as HTMLElement;

            // If we've hit the bottom of the scroll, check and load in more months
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 1) {
              this.loadMoreMonths(false);
            }

            // If we've hit the top of the scroll, check and load in past months
            if (target.scrollTop === 0) {
              const firstMonth = this.months[0];
              const lastDayOfFirstMonth = firstMonth[firstMonth.length - 1];
              const threeYearsAgo = subMonths(new Date(), 36);

              // Check if we're not going beyond 3 years in the past
              if (isBefore(threeYearsAgo, lastDayOfFirstMonth)) {
                this.loadMoreMonths(true);
              } else {
                this.toast.open("You've reached the maximum viewable date range", 'Dismiss', NotificationType.Error);
              }
            }
          });
      }, 100);
    });
  }

  ngOnDestroy() {
    this.timeout?.unsubscribe?.();
  }

  private fetchTasks(start = this.startDate, monthCount = this.monthCount) {
    const propertyIds = [this.property.id];

    const previousTasks = this.property.tasks;
    const previousStart = this.startDate;

    this.taskService
      .getTasksByDate(start, endOfMonth(addMonths(start, monthCount - 1)), propertyIds, false, false)
      .subscribe((tasks) => {
        if (!previousTasks || previousStart === start) {
          // We need this collective view for some pipes to work
          this.property.tasks = tasks;
        } else {
          const combinedTasks = (this.property.tasks = previousTasks.concat(tasks));

          // Remove any duplicates, if we have any. Keep the last one (likely to be newer since its fresh from the API)
          this.property.tasks = combinedTasks.filter(
            (task, index, array) => array.findLastIndex((v2) => v2.uuid === task.uuid) === index
          );
        }

        this.recreateTaskByDayIndex(this.property.tasks);
      });
  }

  public recreateTaskByDayIndex(tasks: any[]): void {
    const tasksByDate = {};

    tasks.forEach((task) => {
      const date = task.start_date.substr(0, 10);

      if (!tasksByDate[date]) {
        tasksByDate[date] = [];
      }

      tasksByDate[date].push(task);
    });

    this.tasks = tasksByDate;

    // If a day is open in the sidebar (it usually is), we need to refresh the data in the child element
    if (this.mode === 'tasks' && this.selectedDates) {
      this.dateClicked(this.selectedDates[0], this.property, this.tasks[this.selectedDates[0]]);
    }
  }

  public reservationClicked(reservation) {
    this.selectedDates = null;
    if (!this.conflictingEventsMap[reservation.uuid]?.length) {
      this.selectedReservation = reservation;
      return;
    }

    // open conflicting events dialog
    const dialogRef = this.dialog.open(ConflictingEventsComponent, {
      data: {
        events: this.conflictingEventsMap[reservation.uuid],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.selectedDates = null;
      this.selectedReservation = result;
    });
  }

  public clearSelected() {
    this.selectedDates = null;
    this.daysTasks = null;
    this.selectedReservation = null;
    if (this.mode === 'tasks' && this.dynamicPricingVisible$.value) {
      this.dynamicPricingVisible$?.next(false);
      // reset sidebar view
      this.eventsService.fire(`dynamic-pricing-sidebar-view-force-${this.property.id}`, undefined);
    }
  }

  public savePriceAndAvailability(values: SidebarPricingSaveEvent) {
    this.selectedDates = values.dates;
    this.desiredPrice = values.price;
    this.desiredAvailability = values.availability;
    this.desiredLengthOfStay = values.los;
    this.desiredMaxLengthOfStay = values.maxStay;
    this.desiredNote = values.note;
    this.daysOfTheWeek = values.daysOfTheWeek;

    this.saving = true;

    this.propertiesService
      .savePricingAndAvailability({
        propertyId: this.property.id,
        start: format(this.selectedDates[0]),
        end: format(this.selectedDates[this.selectedDates.length - 1]),
        daysOfTheWeek: this.daysOfTheWeek,
        available: this.desiredAvailability,
        price: this.desiredPrice,
        lengthOfStay: this.desiredLengthOfStay,
        maxLengthOfStay: this.desiredMaxLengthOfStay,
        note: this.desiredNote,
        closed_for_checkin: values.closedForCheckin,
        closed_for_checkout: values.closedForCheckout,
      })
      .subscribe(
        (res: any) => {
          if (res.success) {
            const failureTypes = [
              this.desiredAvailability !== null ? this.FAILURE_TYPES.available : null,
              this.desiredPrice ? this.FAILURE_TYPES.price : null,
              this.desiredLengthOfStay ? this.FAILURE_TYPES.minimum_length_of_stay : null,
            ].filter((x) => x !== null);

            // Refetch the dates that have changed. We cannot calculate this locally, as we have dynamic price rules on the backend
            this.refetchDates(
              this.property,
              this.selectedDates[0],
              this.selectedDates[this.selectedDates.length - 1],
              failureTypes
            );
          } else {
            this.saving = false;
          }
        },
        (error) => {
          this.saving = false;
        }
      );
  }

  refetchDates(property: any, startDate: any, endDate: any, failureTypes?: string[]) {
    property.loading = true;
    this.propertiesService.getPricingAndAvailability(property.id, startDate, endDate).subscribe((res) => {
      this.property = Object.assign(this.property, res);
      this.pricingAndAvailability = Object.assign(this.pricingAndAvailability, res);

      let hasNewFailures = false;

      // check for failures
      if (failureTypes?.length > 0) {
        Object.values(res).forEach((day: any) => {
          if (!day.failures) {
            day.new_failures = [];
            return;
          }

          day.new_failures = day.failures.filter((failure) => {
            return failureTypes.indexOf(failure.type) > -1;
          });

          if (day.new_failures?.length > 0 && !hasNewFailures) {
            hasNewFailures = true;
          }
        });
      }

      if (!hasNewFailures) {
        this.clearSelected();
      }
      this.saving = false;
    });
  }

  dateClicked(date: string, property: string, daysTasks?: any) {
    if (this.mode !== 'tasks' && !this.hasCalendarAccess) {
      return;
    }

    if (this.dynamicPricingVisible$.value) {
      return;
    }

    // If we're in task mode
    if (this.mode === 'tasks') {
      this.selectedDates = [date];
      this.daysTasks = daysTasks;
      return;
    }

    this.selectedReservation = null;

    // Mobile, allowing click again on the same date
    if (this.selectedDates?.length > 0 && date === this.selectedDates[0]) {
      this.selectedDates.push(date);
      return;
    }

    if (this.selectedDates?.length === 1) {
      let dates;

      if (isBefore(this.selectedDates[0], date)) {
        dates = eachDay(this.selectedDates[0], date);
      } else {
        dates = eachDay(date, this.selectedDates[0]);
      }

      this.selectedDates = dates.map((date) => {
        return format(date);
      });
    } else {
      this.selectedDates = [date];
    }
  }

  dateHovered(date: Date) {
    let dates;

    if (!this.selectedDates || this.selectedDates.length > 1) {
      this.hoveredDates = [];
      return;
    }

    if (isBefore(this.selectedDates[0], date)) {
      dates = eachDay(this.selectedDates[0], date);
    } else {
      dates = eachDay(date, this.selectedDates[0]);
    }

    this.hoveredDates = dates.map((date) => {
      return format(date);
    });
  }

  fetchPricingAndAvailability(additive = false, start = this.startDate, monthCount = this.monthCount) {
    this.propertiesService
      .getPricingAndAvailability(this.property.id, start, endOfMonth(addMonths(start, monthCount - 1)))
      .subscribe((res) => {
        if (additive) {
          this.pricingAndAvailability = {
            ...this.pricingAndAvailability,
            ...res,
          };

          this.property = Object.assign(this.property, this.pricingAndAvailability);
        } else {
          this.loadingPricing = false;
          this.pricingAndAvailability = res;
          this.property = Object.assign(this.property, res);
        }
      });
  }

  getReservations(start = this.startDate, monthCount = this.monthCount) {
    this.reservationService
      .getReservationsByDate(start, endOfMonth(addMonths(start, monthCount - 1)), [this.property.id], false, true, true)
      .subscribe((res) => {
        this.reservationsArray = [...this.reservationsArray, ...res];
        this.conflictingEventsMap = mapConflictingEvents(this.reservationsArray);

        const events = res;

        events.forEach((event) => {
          const startDate = event.checkin.split('T')[0];

          if (this.reservations[startDate]?.some((e) => e.uuid === event.uuid)) {
            // we already have this event, return
            return;
          }

          const sameWeek = isSameWeek(event.checkin, event.checkout, {
            weekStartsOn: this.startOfWeekPref,
          });

          if (event.listing.property_id !== this.property.id) {
            event.isParentChildReservation = true;
          }

          if (sameWeek) {
            event.display_start = format(event.checkin);
            // limit the display end to the end of the month
            const end = isBefore(event.checkout, endOfMonth(event.checkin))
              ? format(event.checkout)
              : format(endOfMonth(event.checkin));
            event.display_end = end;
            this.addEventToDate(event, startDate);

            if (!isSameMonth(event.checkin, event.checkout)) {
              // same week, but broken up by a month - we need to split the event
              const secondEvent = { ...event };
              secondEvent.display_start = format(startOfMonth(event.checkout));
              secondEvent.display_end = format(event.checkout);
              this.addEventToDate(secondEvent, secondEvent.display_start);
            }
          } else {
            this.splitEventIntoWeekBlocks(event);
          }
        });
      });
  }

  private splitEventIntoWeekBlocks(event: any) {
    let start = format(event.checkin);
    // start with end of checkin week
    let end = format(
      endOfWeek(event.checkin, {
        weekStartsOn: this.startOfWeekPref,
      })
    );

    event.display_start = start;
    // Extend the end so that it disappears off the edge of the calendar
    event.display_end = format(addDays(endOfWeek(end, { weekStartsOn: this.startOfWeekPref }), 1));

    this.addEventToDate(event, start);
    // First week event creation complete

    if (!isSameMonth(event.display_start, event.display_end)) {
      event.display_start = format(startOfMonth(event.display_end));
      if (
        // if the event spans the whole week
        !isSameWeek(event.display_start, format(event.checkout), { weekStartsOn: this.startOfWeekPref })
      ) {
        event.display_end = format(addDays(endOfWeek(event.display_start), 1));
        this.addEventToDate(event, event.display_start);
      }
    }

    // while the end of the week is before the checkout date
    const maxIterations = 1000; // failsafe to be sure that this while statement completes eventually, timezones and daylight savings be damned
    let i = 0;
    while (isBefore(end, event.checkout) && !isSameDay(end, event.checkout) && i < maxIterations) {
      i++;
      // start of the week after the last end
      start = format(
        startOfWeek(addWeeks(end, 1), {
          weekStartsOn: this.startOfWeekPref,
        })
      );

      // update end to be the end of our new week
      end = format(
        endOfWeek(start, {
          weekStartsOn: this.startOfWeekPref,
        })
      );

      // if the checkout is before the end of the week, use the checkout date
      // otherwise use the end of the week
      end = isBefore(event.checkout, end)
        ? format(event.checkout)
        : format(
          endOfWeek(start, {
            weekStartsOn: this.startOfWeekPref,
          })
        );
      event.display_start = start;
      event.display_end = end;
      // Add another week block to the list
      this.addEventToDate(event, start);

      if (!isSameMonth(event.display_start, event.display_end)) {
        event.display_start = format(startOfMonth(end));
        event.display_end = end;
        this.addEventToDate(event, event.display_start);
      }
    }
  }

  private buildMonths(startDate = this.startDate, monthsToLoad = this.monthCount, scrollToThisMonth = false) {
    this.months = [...this.months, ...this.buildMonthsArray(startDate, monthsToLoad)];

    setTimeout(() => {
      this.loadingMoreMonths = false;
    }, 500);

    if (scrollToThisMonth) {
      setTimeout(() => {
        this.scrollToThisMonth();
      }, 250);
    }
  }

  private buildMonthsArray(startDate: Date, monthCount: number): string[][] {
    const months: string[][] = [];

    for (let index = 0; index < monthCount; index++) {
      const monthStart = addMonths(startDate, index);
      const startOfMonthWeek = startOfWeek(monthStart, {
        weekStartsOn: this.startOfWeekPref,
      });
      const monthEnd = endOfMonth(monthStart);

      let days: Date[] | string[] = eachDay(startOfMonthWeek, monthEnd);

      days = days.map((day) => {
        return format(day);
      });

      days.forEach((day, index) => {
        if (!isSameMonth(day, monthStart)) {
          days[index] = null;
        }
      });

      months.push(days);
    }

    return months;
  }

  scrollToThisMonth() {
    const approximateMonthHeight = 1750;
    try {
      const monthElm = document.querySelector('.this-month');
      const target = monthElm
        ? monthElm.getBoundingClientRect().top + this.calendarContent.nativeElement.scrollTop - 100
        : approximateMonthHeight;
      this.calendarContent.nativeElement.scrollTo(0, target);
    } catch (err) {
      this.calendarContent.nativeElement.scrollTo(0, approximateMonthHeight);
    }
  }

  propertyChanged(newPropertyId) {
    if (this.source) {
      this.router.navigate(['/calendar/property', newPropertyId], { queryParams: { source: this.source } });
    } else {
      this.router.navigate(['/calendar/property', newPropertyId]);
    }
  }

  loadMoreMonths(reverse: boolean) {
    this.loadingMoreMonths = true;
    let newStartDate;
    const monthsToLoad = 3;

    if (!reverse) {
      const lastExistingMonth = this.months[this.months.length - 1];
      const lastDay = lastExistingMonth[lastExistingMonth.length - 1];

      newStartDate = addDays(lastDay, 1);
    } else {
      const firstMonth = this.months[0];
      const firstDayOfFirstMonth = firstMonth.find((day) => day !== null);

      // When loading past months, we want to start from 3 months before the current first day
      newStartDate = startOfMonth(subMonths(firstDayOfFirstMonth, monthsToLoad));

      // Prepend the new months to the beginning of the array
      this.months = [...this.buildMonthsArray(newStartDate, monthsToLoad), ...this.months];

      // Maintain scroll position when adding content to the top
      setTimeout(() => {
        const approximateMonthHeight = 583; // Height of one month
        this.calendarContent.nativeElement.scrollTop = approximateMonthHeight * monthsToLoad;
      });

      this.loadingMoreMonths = false;
      this.fetchPricingAndAvailability(true, newStartDate, monthsToLoad);
      this.getReservations(newStartDate, monthsToLoad);
      this.fetchTasks(newStartDate, monthsToLoad);
      return;
    }

    this.buildMonths(newStartDate, monthsToLoad);

    this.fetchPricingAndAvailability(true, newStartDate, monthsToLoad);
    this.getReservations(newStartDate, monthsToLoad);
    this.fetchTasks(newStartDate, monthsToLoad);
  }

  close(destination: string) {
    if (destination === 'details') {
      this.router.navigate(['/properties/property', this.property.id]);
    }

    if (destination === 'calendar') {
      this.router.navigate(['/calendar']);
    }
  }

  taskAdded(event: any) {
    if (this.property?.tasks.length > 0) {
      this.property.tasks.push(event);
    } else {
      this.property.tasks = [event];
    }

    this.recreateTaskByDayIndex(this.property.tasks);
  }

  taskUpdated(event: any) {
    if (this.property?.tasks.length > 0) {
      const index = this.property?.tasks.map((x) => x.uuid).indexOf(event.uuid);
      this.property.tasks[index] = event;
    } else {
      this.property.tasks = [event];
    }

    this.recreateTaskByDayIndex(this.property.tasks);
  }

  taskDeleted(uuid: any) {
    if (this.property?.tasks.length > 0) {
      const index = this.property.tasks.map((x) => x.uuid).indexOf(uuid);

      if (index > -1) {
        this.property.tasks = this.property.tasks.filter((ele, i) => i !== index);
      }
    }

    this.recreateTaskByDayIndex(this.property.tasks);
  }

  fetchUpdatedReservationDetails(updatedReservation: any) {
    this.selectedReservation = null;
    this.clearReservations();
    this.getReservations();
  }

  private clearReservations() {
    this.reservations = {};
    this.reservationsArray = [];
  }

  private addEventToDate(event: any, dateProp: string) {
    if (!this.reservations[dateProp]?.length) {
      this.reservations[dateProp] = [];
    }
    this.reservations[dateProp].push({ ...event });
  }

  public getParentPath(source: string, propertyId: string) {
    if (source === 'p') {
      return `/properties/property/${propertyId}/merge-match`;
    } else {
      return `/calendar`;
    }
  }


  public checkPropertyAndPreviewDynamicPricing() {
    const { in_market, data_errors } = this.$dynamicPricingOptions();
    if (!in_market || data_errors) {
      this.presentDynamicPricingErrorModal();
    } else {
      this.previewDynamicPricing();
    }
  }

  private presentDynamicPricingErrorModal() {
    const dialogRef = this.dialog.open(DynamicPricingPropertyErrorModalComponent, {
      disableClose: true,
      height: '100vh',
      width: '100vw',
      maxWidth: '100%',
      maxHeight: '100%',
      data: {
        property: this.property,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'contact-support') {
        this.intercomService.show('');
      }
    });
  }

  public previewDynamicPricing(
    basePriceType: 'conservative' | 'recommended' | 'aggressive' = 'recommended',
    nextState?: boolean,
    min_price?: number,
    max_price?: number,
    base_price?: number
  ) {
    const visible = nextState ?? !this.dynamicPricingVisible$.value;
    this.eventsService.fire(`dynamic-pricing-visible-change-${this.property.id}`, visible);
    if (visible) {
      this.fetchDynamicPricingPreview(basePriceType, min_price, max_price, base_price);
      this.eventsService.fire(`dynamic-pricing-sidebar-view-force-${this.property.id}`, 'dynamic-pricing');
    }
  }

  public fetchDynamicPricingPreview(
    basePriceType: 'conservative' | 'recommended' | 'aggressive' | 'custom' = 'recommended',
    min_price?: number,
    max_price?: number,
    base_price?: number
  ) {
    if (this._dynamicPricingSuggestionRequestSub) {
      this._dynamicPricingSuggestionRequestSub.unsubscribe();
    }

    this.dynamicPricingSuggestionLoading = true;
    this._dynamicPricingSuggestionRequestSub = this.propertiesService
      .getDynamicPricingPreview(
        this.property.id,
        new Date(),
        addDays(new Date(), DYNAMIC_PRICING_PREVIEW_DATE_RANGE),
        basePriceType,
        min_price,
        max_price,
        base_price
      )
      .pipe(takeUntil(this.destroyed$), delay(1600))
      .subscribe(
        (res) => {
          this.setDynamicPriceMap(res as DynamicPricingPreview);
          this.dynamicPricingSuggestionLoading = false;
        },
        (err) => {
          this.dynamicPricingSuggestionLoading = false;
        }
      );
  }

  public isLegacy$ = combineLatest([this.billingFacade.isOnProfessionalPlan$, this.billingFacade.isOnHostPlan$]).pipe(
    map(([isOnProfessionalPlan, isOnHostPlan]) => !isOnProfessionalPlan && !isOnHostPlan)
  );

  public upgradeplan() {
    this.router.navigate(['/settings/your-plan']);
  }

  public setDynamicPriceMap(dynamicPriceMap: DynamicPricingPreview) {
    this.dynamicPriceMap$.next(dynamicPriceMap);
  }

  getPriceComparisonColor(currentPrice: number, futurePrice: number): string {
    if (!currentPrice || !futurePrice) {
      return 'text-default';
    }

    const percentageDifference = ((futurePrice - currentPrice) / currentPrice) * 100;

    if (percentageDifference > 10) {
      return 'text-green-500';
    } else if (percentageDifference < -10) {
      return 'text-red-500';
    }
    return 'text-default';
  }

  public isDynamicPricingSource(sources: any[]) {
    return sources.find((source) => source?.type === 'price' && source?.source === 'manual') !== undefined;
  }

  public resetToDynamicPricing() {
    this.saving = true;
    this.propertiesService
      .resetToDynamicPricing(this.property.id, this.selectedDates[0], this.selectedDates[this.selectedDates.length - 1])
      .subscribe((res) => {
        this.refetchDates(this.property, this.selectedDates[0], this.selectedDates[this.selectedDates.length - 1]);
        this.saving = false;
      });
  }
}
